/**
 * The ad-wall shaders.
 *
 * Kept beside the component rather than inside it: GLSL is a different
 * language with its own reasoning, and threading 90 lines of it through a
 * React file makes both harder to read. The fragment shader owns the wall
 * mapping and the flip; everything the host decides reaches it as a uniform.
 */

import { COLS, CREATIVES, ROWS, SLOT_H, SLOT_W } from "./adWallAtlas";

export const VERT = `
attribute vec2 a_pos;
void main(){ gl_Position = vec4(a_pos, 0.0, 1.0); }
`;

/**
 * The atlas's shape, written into the shader from the one place it is
 * decided. A wall cell has the slot's proportions, so a 9:16 creative fills
 * its card without a crop.
 */
const ATLAS = `
const float N_CREATIVES = ${CREATIVES.length}.0;
const float ATLAS_COLS = ${COLS}.0;
const float ATLAS_ROWS = ${ROWS}.0;
const float CELL_ASPECT = ${(SLOT_H / SLOT_W).toFixed(6)};
`;

export const FRAG = `
precision highp float;
${ATLAS}
uniform vec2 u_res, u_mouse, u_guardC, u_guardS;
uniform float u_time, u_hover, u_dpr;
uniform vec3 u_bg, u_c1, u_c2;
uniform float u_glow, u_guardMin;
uniform sampler2D u_badge;
uniform sampler2D u_tex;

float hash(float n){ return fract(sin(n) * 43758.5453); }
float hash2(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float TW(){ return 190.0 * u_dpr; }
float TH(){ return TW() * CELL_ASPECT; }

void wallCell(vec2 q, out float col, out float row, out vec2 f, out float off){
  col = floor(q.x / TW());
  float dir = mod(col, 2.0) < 0.5 ? 1.0 : -1.0;
  off = hash(col * 7.31 + 1.0) * TH() * 3.0 + u_time * 16.0 * u_dpr * dir;
  float ly = q.y + off;
  row = floor(ly / TH());
  f = vec2(fract(q.x / TW()), fract(ly / TH()));
}

// Which creative a wall cell shows, and where it sits on the atlas.
//
// Every column walks all ten before it repeats, each in its own order: the
// row is stepped by 7, which shares no factor with 10, so ten consecutive
// cells are ten different ads; the column shifts the start by 3 and the hash
// scrambles where each column begins, so the same ad rarely lands beside
// itself. Ten cards is not many for a wall, and this is what keeps it from
// reading as five copies of the same strip.
vec2 tileUV(float col, float row, vec2 f){
  float tr = mod(row + floor(hash(col * 3.1) * N_CREATIVES), N_CREATIVES);
  float idx = mod(col * 3.0 + tr * 7.0, N_CREATIVES);
  float sc = mod(idx, ATLAS_COLS);
  float sr = floor(idx / ATLAS_COLS);
  f = clamp(f, 0.002, 0.998);
  return vec2((sc + f.x) / ATLAS_COLS, (sr + f.y) / ATLAS_ROWS);
}

void main(){
  vec2 q = vec2(gl_FragCoord.x, u_res.y - gl_FragCoord.y);
  vec2 M = u_mouse;
  vec2 dv = q - M;
  float R = 210.0 * u_dpr;
  vec3 gc = mix(u_c1, u_c2, 0.5 + 0.5 * sin(atan(dv.y, dv.x) + u_time * 0.6));

  float c, r, o; vec2 f;
  wallCell(q, c, r, f, o);
  vec4 s = texture2D(u_tex, tileUV(c, r, f));

  // THE REVEAL IS A LAMP, not a card opening and not a cut-out.
  //
  // Each card used to scale in horizontally from its own centre, so the
  // boundary of the reveal was made of card edges — a ragged staircase of
  // rectangles rather than a shape. One mask over the whole wall reads as
  // light falling on it, and the grid stops behaving like forty things that
  // flip.
  //
  // FALLOFF, NOT AN EDGE. A smoothstep between two radii is a hole with soft
  // sides: it has a place where the light stops, and the eye finds it. Real
  // light has no such place — it obeys an inverse square and simply gets
  // fainter until the room swallows it. So this is 1/(1+kd^2), which is
  // bright and almost flat across the middle, falls away hard through the
  // mid-distance, and trails off for a very long way.
  //
  // The tiny floor subtraction is the room swallowing it. Without it the tail
  // never quite reaches zero and the whole wall sits at two percent lit,
  // which robs the dark of its darkness.
  float d = length(dv) / R;
  float fall = 1.0 / (1.0 + d * d * 1.9);
  fall = max(0.0, fall - 0.08) / 0.92;

  // Barely-there irregularity. A mathematically perfect circle reads as a
  // mask even with a soft falloff, because nothing physical is that exact —
  // but the wobble that made it a blob was far too much. Three percent is
  // enough to break the perfection and not enough to see as a shape.
  float ang = atan(dv.y, dv.x);
  float wob = 0.03 * sin(ang * 3.0 + u_time * 0.21)
            + 0.02 * sin(ang * 5.0 - u_time * 0.17);
  float spot = clamp(u_hover * fall * (1.0 + wob), 0.0, 1.0);

  vec3 card = mix(u_bg, s.rgb, s.a);
  vec3 A = mix(u_bg, card, 0.55 + 0.45 * spot);
  A = mix(u_bg, A, 0.92);
  float m = spot;

  float guard = mix(u_guardMin, 1.0, smoothstep(0.75, 1.25, length((q - u_guardC) / u_guardS)));

  vec3 col = mix(u_bg, A, m * guard);

  // The hot centre. Directly under a lamp a surface is not just visible, it
  // is washed out slightly toward the light's own colour — that is most of
  // what makes something look lit rather than merely drawn.
  // Brighter, and reaching further. The wash is toward the light's colour
  // lifted well toward white — a lamp's hot spot is whiter than its throw,
  // and mixing purely toward the gradient tinted the middle violet instead
  // of lighting it.
  float core = 1.0 - smoothstep(0.0, 1.15, d);
  col = mix(col, mix(gc, vec3(1.0), 0.40), core * 0.26 * s.a * guard);

  // No rim. A blob needed one to describe its outline; a lamp that draws a
  // ring where its light runs out is a lamp with an edge, which is the thing
  // this stopped being.

  // THE LIFECYCLE, on top of the tiles rather than in them.
  //
  // Each card runs one slow loop: a take goes up, and it pays. Nothing in the
  // atlas changes — this is drawn over whatever the tile already holds, so the
  // cards stay exactly as designed.
  //
  // Phase is hashed per card so the wall never pulses in unison. A grid that
  // breathes together reads as one object flashing; staggered, it reads as
  // many creators working at their own pace, which is the thing being
  // described.
  float ph = hash(c * 17.3 + r * 3.7 + 0.5);
  float cyc = fract(u_time * 0.14 + ph);

  // Upload: a soft band climbing the card. Bottom to top, because that is the
  // direction a thing being sent travels, and it resolves at the top where
  // the result would appear. Present only for the first two-thirds, so the
  // card is quiet before it starts again.
  float rise = smoothstep(0.0, 0.66, cyc);
  float band = exp(-pow((f.y - (1.0 - rise)) * 11.0, 2.0));
  float upload = band * (1.0 - smoothstep(0.62, 0.72, cyc));

  // Paid: a green badge lifting off the card and fading.
  //
  // Sampled from its own small texture rather than drawn here. A plus is two
  // rectangles and a dollar sign is not — at this size a shader-drawn one
  // looks like an approximation, which is exactly what it is. Canvas draws
  // the pill and sets the type once; this just places it.
  //
  // Off to the side, never centred: the middle of every tile is a person's
  // face, and a badge parked on it looks like a sticker rather than a number
  // floating up.
  float pop = smoothstep(0.58, 0.66, cyc) * (1.0 - smoothstep(0.90, 1.0, cyc));
  float riseY = 0.66 - (cyc - 0.58) * 0.34;
  vec2 g = vec2(f.x - 0.68, (f.y - riseY) * 1.6);

  // Half-size in tile-width units. The texture is 2:1 and so is this, so the
  // badge keeps its proportions whatever the tile is scaled to.
  vec2 hs = vec2(0.20, 0.10);
  vec2 bl = g / (hs * 2.0) + 0.5;
  float inB = step(0.0, bl.x) * step(bl.x, 1.0) * step(0.0, bl.y) * step(bl.y, 1.0);
  vec4 bs = texture2D(u_badge, clamp(bl, 0.002, 0.998));

  // The band stays faint — it is a wash over a whole card, and at any real
  // strength it fights the copy in front of it. The glyphs carry far more
  // weight because they are small and shaped: a few hundred pixels that mean
  // something beat a full-card sheen that means nothing.
  float wash = upload * 0.09 * m * guard * s.a;
  col = mix(col, gc, clamp(wash, 0.0, 1.0));
  // The guard is softened for the badge rather than applied in full. Its job
  // is keeping the copy readable, and a small badge is not what threatens
  // that — dimmed to 0.3 near the form it simply vanished, which is the whole
  // point of it lost to a rule aimed at something else.
  float markGuard = mix(0.6, 1.0, guard);
  // Held back to roughly half. At full strength a saturated green pill is the
  // brightest thing on a dim wall and pulls the eye off the form — which is
  // the opposite of ambient. Translucent, it reads as something happening
  // behind the page rather than on it.
  float earned = clamp(bs.a * inB * pop * m * markGuard, 0.0, 1.0) * 0.55;
  col = mix(col, bs.rgb, earned);
  col += (hash2(q + fract(u_time)) - 0.5) * 0.012;
  gl_FragColor = vec4(col, 1.0);
}
`;

/** Compile one stage, returning null rather than throwing on a bad source. */
export function compile(gl: WebGLRenderingContext, type: number, src: string) {
  const sh = gl.createShader(type);
  if (!sh) return null;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    gl.deleteShader(sh);
    return null;
  }
  return sh;
}
